<template>
  <b-button
    :variant="variant"
    :size="size"
    :disabled="disabled || loading"
    :class="buttonClass"
    v-bind="$attrs"
    @click="handleClick"
  >
    <template v-if="loading">
      <LoadingSpinner
        :size="computedSpinnerSize"
        :variant="computedSpinnerVariant"
        :label="loadingLabel"
      />
      <span v-if="loadingText" class="ml-2">{{ loadingText }}</span>
    </template>
    <template v-else>
      <slot />
    </template>
  </b-button>
</template>

<script>
import LoadingSpinner from './LoadingSpinner.vue'

export default {
  name: 'LoadingButton',
  components: {
    LoadingSpinner
  },
  inheritAttrs: false,
  props: {
    /**
     * Whether the button is in loading state
     */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * Text to display during loading
     */
    loadingText: {
      type: String,
      default: ''
    },
    /**
     * Bootstrap button variant
     */
    variant: {
      type: String,
      default: 'primary'
    },
    /**
     * Bootstrap button size
     */
    size: {
      type: String,
      default: null
    },
    /**
     * Whether the button is disabled
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Spinner size (auto-calculated from button size)
     */
    spinnerSize: {
      type: String,
      default: null,
      validator: (value) => value === null || ['sm', 'default'].includes(value)
    },
    /**
     * Spinner variant (auto-calculated for contrast)
     */
    spinnerVariant: {
      type: String,
      default: null
    },
    /**
     * Accessibility label for loading spinner
     */
    loadingLabel: {
      type: String,
      default: 'Loading...'
    }
  },
  computed: {
    computedSpinnerSize() {
      if (this.spinnerSize) return this.spinnerSize
      return this.size === 'sm' ? 'sm' : 'default'
    },
    computedSpinnerVariant() {
      if (this.spinnerVariant) return this.spinnerVariant
      // Use light spinner for dark variants
      return ['primary', 'secondary', 'success', 'info', 'warning', 'danger'].includes(this.variant)
        ? 'light'
        : 'primary'
    },
    buttonClass() {
      return {
        'loading-button': true,
        'loading-button--loading': this.loading
      }
    }
  },
  methods: {
    handleClick(event) {
      if (this.loading) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      this.$emit('click', event)
    }
  }
}
</script>

<style lang="scss" scoped>
.loading-button {
  position: relative;

  &--loading {
    // Ensure consistent button appearance during loading
  }

  .spinner-border {
    vertical-align: text-bottom;
  }
}
</style>